import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

import { SynopticWindowButtonComponent } from '@connect-services/client/components/synoptic/synoptic-window-button/synoptic-window-button.component';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { HomeService } from '@connect-services/client/services/home.service';
import { DepositWithStandard } from '@connect-services/client/services/synoptic/export-synoptic-cartouche.service';
import { SelectSynopticService } from '@connect-services/client/services/synoptic/select-synoptic.service';
import { SelectComponent } from '@connect-services/src/lib/components/select/select.component';
import { SynopticZone } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RessourcesModule, SelectComponent, SynopticWindowButtonComponent],
    selector: 'app-select-synoptic-panel',
    styleUrls: ['./select-synoptic-panel.component.scss'],
    templateUrl: './select-synoptic-panel.component.html',
})
export class SelectSynopticPanelComponent {
    protected readonly depositOptions = computed(() =>
        this.selectSynopticService.depositList().map((depositWithStandard: DepositWithStandard) => ({
            label: depositWithStandard.name,
            value: depositWithStandard,
        })),
    );
    protected readonly homeService = inject(HomeService);
    protected readonly RoutePathEnum = RoutePathEnum;
    protected readonly selectedDepositOption = computed(() =>
        this.selectSynopticService.selectedDeposit()
            ? [
                  {
                      label: this.selectSynopticService.selectedDeposit()!.name,
                      value: this.selectSynopticService.selectedDeposit()!,
                  },
              ]
            : [],
    );
    protected readonly selectedZoneOption = computed(() =>
        this.selectSynopticService.selectedSynopticZone()
            ? [
                  {
                      label: this.selectSynopticService.selectedSynopticZone()!.zn_nroref,
                      value: this.selectSynopticService.selectedSynopticZone()!,
                  },
              ]
            : [],
    );
    protected readonly selectSynopticService = inject(SelectSynopticService);
    protected readonly title = computed<string>(() => {
        let translationKey = 'selectSynopticZone';
        if (!this.selectSynopticService.synopticZones().length) {
            translationKey = 'selectSynopticDeposit';
        }

        if (this.isGraceThdSynoptic(this.selectSynopticService.selectedDeposit())) {
            translationKey = 'selectSynopticNRO';
        }

        return this.translocoService.translate(translationKey);
    });
    protected readonly zoneOptions = computed(() =>
        this.selectSynopticService.synopticZones().map((synopticZone: SynopticZone) => ({
            label: synopticZone.zn_nroref,
            value: synopticZone,
        })),
    );
    protected readonly zoneSelectPlaceholder = computed<string>(() => {
        let translationKey = 'selectZone';
        if (this.isGraceThdSynoptic(this.selectSynopticService.selectedDeposit())) {
            translationKey = 'selectNRO';
        }

        return this.translocoService.translate(translationKey);
    });

    private readonly translocoService = inject(TranslocoService);

    protected async onDepositSelection(deposit?: DepositWithStandard): Promise<void> {
        if (!deposit) {
            this.selectSynopticService.clearSelections();

            return;
        }

        await this.selectSynopticService.selectSynopticZone(deposit);
    }

    protected async onSynopticZoneSelection(zone?: SynopticZone): Promise<void> {
        if (!zone) {
            this.selectSynopticService.resetSynopticSelection();

            return;
        }

        await this.selectSynopticService.selectSynoptic(zone);
    }

    private isGraceThdSynoptic(selectedDeposit: DepositWithStandard | null): boolean {
        return !!selectedDeposit?.standard?.alias?.includes('gracethd');
    }
}
